import React, {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import Cropper from 'react-easy-crop';
import {
  DEFAULT_MAX_BYTES,
  IMAGE_MIME_TYPES,
  UploadError,
  blobToFile,
  cropImageToBlob,
  fileToObjectUrl,
  formatBytes,
  uploadFile,
  validateFile,
} from '../lib/uploads';
import { pickErrorMessage } from '../lib/apiHelpers';
import { Modal } from './Modal';

function acceptAttr(accept) {
  return Array.isArray(accept) && accept.length ? accept.join(',') : 'image/*';
}

/**
 * Image picker → fixed-aspect cropper → deferred GCS upload.
 *
 * The component never uploads on its own. Hand it a ref and call
 * `ref.current.commit()` from your Save handler — it will:
 *   - return the existing `value` if nothing changed
 *   - or upload the cropped pending file and return the new public URL
 *
 * Props:
 *   value                public URL of the saved image (string)
 *   onChange(url)        fires when the final URL changes (after commit, or when cleared)
 *   folder               GCS folder, e.g. "menu-items"
 *   label, helpText, disabled
 *   accept (MIME[]), maxBytes
 *   aspect               crop aspect (default 1 → square)
 *   outputSize           { width, height } target output (default 800x800)
 *   outputType           'image/jpeg' | 'image/png' | 'image/webp' (default 'image/jpeg')
 *   outputQuality        0..1 for JPEG/WebP (default 0.9)
 *   previewSize          square preview size in px (default 96)
 *   onUploadStateChange(bool)  flips true while commit() is in flight
 *   onPendingChange(bool)      flips true when a cropped image is waiting to upload
 */
const ImageUploadField = forwardRef(function ImageUploadField(props, ref) {
  const {
    value,
    onChange,
    folder,
    label = 'Image',
    helpText,
    disabled = false,
    accept = IMAGE_MIME_TYPES,
    maxBytes = DEFAULT_MAX_BYTES,
    aspect = 1,
    outputSize = { width: 800, height: 800 },
    outputType = 'image/jpeg',
    outputQuality = 0.9,
    previewSize = 96,
    onUploadStateChange,
    onPendingChange,
    id: idProp,
  } = props;

  const reactId = useId();
  const inputId = idProp || `image-upload-${reactId}`;
  const fileInputRef = useRef(null);

  const [pickerSrc, setPickerSrc] = useState('');
  const [pickerName, setPickerName] = useState('');
  const [cropOpen, setCropOpen] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [cropping, setCropping] = useState(false);

  const [pending, setPending] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');
  const [previewError, setPreviewError] = useState(false);

  useEffect(() => {
    setPreviewError(false);
  }, [value]);

  useEffect(() => {
    onUploadStateChange?.(uploading);
  }, [uploading, onUploadStateChange]);

  useEffect(() => {
    onPendingChange?.(Boolean(pending));
  }, [pending, onPendingChange]);

  useEffect(
    () => () => {
      if (pickerSrc) URL.revokeObjectURL(pickerSrc);
    },
    [pickerSrc],
  );

  useEffect(
    () => () => {
      if (pending?.previewUrl) URL.revokeObjectURL(pending.previewUrl);
    },
    [pending],
  );

  const pendingRef = useRef(null);
  useEffect(() => {
    pendingRef.current = pending;
  }, [pending]);

  const valueRef = useRef(value);
  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  const helpDefault = useMemo(() => {
    const types = acceptAttr(accept)
      .split(',')
      .map((t) => t.trim().replace(/^image\//, '').toUpperCase())
      .filter(Boolean)
      .join(', ');
    const size = `${outputSize.width}×${outputSize.height}`;
    return `Drag &amp; drop or click to upload. Output ${size}. ${types || 'Images'} up to ${formatBytes(maxBytes)}.`;
  }, [accept, maxBytes, outputSize]);

  const openPicker = useCallback(() => {
    if (disabled || uploading) return;
    fileInputRef.current?.click();
  }, [disabled, uploading]);

  const handleFileInput = useCallback(
    (e) => {
      const file = e.target.files?.[0];
      e.target.value = '';
      if (!file) return;
      try {
        validateFile(file, { accept, maxBytes });
      } catch (err) {
        setError(err instanceof UploadError ? err.message : 'Invalid file.');
        return;
      }
      setError('');
      const url = fileToObjectUrl(file);
      setPickerSrc(url);
      setPickerName(file.name);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedAreaPixels(null);
      setCropOpen(true);
    },
    [accept, maxBytes],
  );

  const onCropComplete = useCallback((_area, areaPixels) => {
    setCroppedAreaPixels(areaPixels);
  }, []);

  const closeCropper = useCallback(() => {
    setCropOpen(false);
    if (pickerSrc) URL.revokeObjectURL(pickerSrc);
    setPickerSrc('');
    setPickerName('');
  }, [pickerSrc]);

  const applyCrop = useCallback(async () => {
    if (!pickerSrc || !croppedAreaPixels) return;
    setCropping(true);
    setError('');
    try {
      const blob = await cropImageToBlob({
        imageSrc: pickerSrc,
        cropPixels: croppedAreaPixels,
        outputSize,
        outputType,
        quality: outputQuality,
      });
      const file = blobToFile(blob, pickerName, outputType);
      if (pending?.previewUrl) URL.revokeObjectURL(pending.previewUrl);
      const previewUrl = URL.createObjectURL(blob);
      setPending({ file, previewUrl });
      closeCropper();
    } catch (err) {
      setError(err instanceof UploadError ? err.message : pickErrorMessage(err));
    } finally {
      setCropping(false);
    }
  }, [
    pickerSrc,
    croppedAreaPixels,
    outputSize,
    outputType,
    outputQuality,
    pickerName,
    pending,
    closeCropper,
  ]);

  const clearPending = useCallback(() => {
    if (pending?.previewUrl) URL.revokeObjectURL(pending.previewUrl);
    setPending(null);
    setError('');
  }, [pending]);

  const handleRemove = useCallback(() => {
    if (uploading) return;
    clearPending();
    setError('');
    onChange?.('');
  }, [clearPending, onChange, uploading]);

  useImperativeHandle(ref, () => ({
    hasPending() {
      return Boolean(pendingRef.current);
    },
    async commit() {
      const p = pendingRef.current;
      if (!p) return valueRef.current || '';
      setUploading(true);
      setProgress(0);
      setError('');
      try {
        const result = await uploadFile({
          file: p.file,
          folder,
          accept,
          maxBytes,
          onProgress: (pct) => setProgress(pct),
        });
        if (p.previewUrl) URL.revokeObjectURL(p.previewUrl);
        setPending(null);
        onChange?.(result.publicUrl);
        return result.publicUrl;
      } catch (err) {
        const msg = err instanceof UploadError ? err.message : pickErrorMessage(err);
        setError(msg || 'Upload failed.');
        throw err;
      } finally {
        setUploading(false);
      }
    },
  }));

  const displayedUrl = pending?.previewUrl || value || '';
  const showPreview = Boolean(displayedUrl) && !(value && previewError && !pending);

  return (
    <div className={`image-upload-field ${disabled ? 'is-disabled' : ''}`.trim()}>
      {label ? (
        <label className="form-label" htmlFor={inputId}>
          {label}
        </label>
      ) : null}

      <div className={`image-upload-drop ${uploading ? 'is-uploading' : ''}`.trim()}>
        <div className="image-upload-preview" style={{ width: previewSize, height: previewSize }}>
          {showPreview ? (
            <img src={displayedUrl} alt="" onError={() => setPreviewError(true)} />
          ) : (
            <div className="image-upload-preview-placeholder" aria-hidden>
              <svg
                width="28"
                height="28"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <circle cx="8.5" cy="9.5" r="1.5" />
                <path d="m21 15-5-5L5 21" />
              </svg>
            </div>
          )}
          {pending ? <span className="image-upload-pending-badge">Pending</span> : null}
        </div>

        <div className="image-upload-body">
          <div className="d-flex flex-wrap gap-2">
            <button
              type="button"
              className="btn btn-sm btn-outline-primary"
              onClick={openPicker}
              disabled={disabled || uploading}
            >
              {value || pending ? 'Change image' : 'Upload image'}
            </button>
            {pending ? (
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={clearPending}
                disabled={uploading}
              >
                Discard change
              </button>
            ) : null}
            {value && !pending ? (
              <button
                type="button"
                className="btn btn-sm btn-outline-danger"
                onClick={handleRemove}
                disabled={uploading}
              >
                Remove
              </button>
            ) : null}
          </div>

          <input
            ref={fileInputRef}
            id={inputId}
            type="file"
            accept={acceptAttr(accept)}
            className="d-none"
            onChange={handleFileInput}
            disabled={disabled || uploading}
          />

          {uploading ? (
            <div className="image-upload-progress mt-2" aria-live="polite">
              <div
                className="progress"
                role="progressbar"
                aria-valuenow={progress}
                aria-valuemin={0}
                aria-valuemax={100}
                style={{ height: 6 }}
              >
                <div className="progress-bar" style={{ width: `${progress}%` }} />
              </div>
              <div className="form-text mt-1">Uploading… {progress}%</div>
            </div>
          ) : (
            <div className="form-text mt-2">
              {pending ? (
                <span className="text-warning-emphasis">
                  Image cropped and ready. It will upload when you save.
                </span>
              ) : (
                <span dangerouslySetInnerHTML={{ __html: helpText || helpDefault }} />
              )}
            </div>
          )}

          {error ? <div className="invalid-feedback d-block mt-1">{error}</div> : null}
          {previewError && value && !pending ? (
            <div className="form-text text-danger mt-1">Could not load preview from the saved URL.</div>
          ) : null}
        </div>
      </div>

      {cropOpen ? (
        <Modal
          title={`Crop & Frame Image (${aspect} : 1)`}
          size="lg"
          onClose={() => !cropping && closeCropper()}
          footer={
            <>
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={closeCropper}
                disabled={cropping}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={applyCrop}
                disabled={cropping || !croppedAreaPixels}
              >
                {cropping ? 'Processing…' : 'Apply Crop & Fit'}
              </button>
            </>
          }
        >
          <div className="image-cropper-wrap">
            <div className="d-flex align-items-center justify-content-between px-1">
              <span className="small text-secondary">
                Fixed Target Ratio: <strong className="text-dark">{aspect} : 1</strong> ({outputSize.width}×{outputSize.height}px)
              </span>
              <span className="badge bg-primary-subtle text-primary border border-primary-subtle small">
                Auto-Fit Ratio Mode
              </span>
            </div>

            <div className="image-cropper-stage position-relative rounded-3 overflow-hidden shadow-inner">
              {pickerSrc ? (
                <Cropper
                  image={pickerSrc}
                  crop={crop}
                  zoom={zoom}
                  aspect={aspect}
                  minZoom={1}
                  maxZoom={4}
                  zoomSpeed={0.3}
                  showGrid={true}
                  restrictPosition
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={onCropComplete}
                  objectFit="contain"
                />
              ) : null}
            </div>

            <div className="image-cropper-controls bg-light p-3 rounded-3 border">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <label className="form-label small fw-semibold mb-0" htmlFor={`${inputId}-zoom`}>
                  Zoom / Scale: <span className="text-primary">{Math.round(zoom * 100)}%</span>
                </label>
                <div className="btn-group btn-group-sm">
                  <button
                    type="button"
                    className="btn btn-outline-secondary px-2"
                    onClick={() => setZoom((z) => Math.max(1, +(z - 0.2).toFixed(2)))}
                    disabled={zoom <= 1}
                    title="Zoom Out"
                  >
                    −
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-secondary px-2"
                    onClick={() => setZoom(1)}
                    disabled={zoom === 1}
                    title="Reset Zoom"
                  >
                    Reset
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-secondary px-2"
                    onClick={() => setZoom((z) => Math.min(4, +(z + 0.2).toFixed(2)))}
                    disabled={zoom >= 4}
                    title="Zoom In"
                  >
                    +
                  </button>
                </div>
              </div>

              <input
                id={`${inputId}-zoom`}
                type="range"
                className="form-range"
                min={1}
                max={4}
                step={0.01}
                value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
              />
              <p className="form-text mb-0 small text-secondary">
                Drag photo to position it within the grid. The output is guaranteed to be saved at exact{' '}
                <strong className="text-dark">{outputSize.width}×{outputSize.height}px ({aspect}:1)</strong>.
              </p>
            </div>
          </div>
        </Modal>
      ) : null}
    </div>
  );
});

export default ImageUploadField;
